import React, { useEffect, useRef, useState } from 'react';
import {
    shuffle,
    useTicker,
    GameResultOverlay,
    GameShell,
} from '../shared';

function SchulteTableGame({
    copy = {},
    initialLevel = 3,
    isPaused = false,
    onRunComplete = null,
    showResults = true,
    ...props
}) {
    const level = Math.max(1, Math.min(3, Math.round(Number(initialLevel) || 3)));
    const sideLength = level + 2;
    const maxNumber = sideLength * sideLength;
    const completionReportedRef = useRef(false);
    const pauseStartedAtRef = useRef(null);
    const pausedDurationRef = useRef(0);
    const [numbers, setNumbers] = useState(() => shuffle(Array.from({ length: maxNumber }, (_, index) => index + 1)));
    const [nextNumber, setNextNumber] = useState(1);
    const [mistakes, setMistakes] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const [finishedAt, setFinishedAt] = useState(null);
    const [tone, setTone] = useState('ready');
    const now = useTicker(Boolean(startedAt && !finishedAt && !isPaused));
    const done = nextNumber > maxNumber;

    const reset = () => {
        completionReportedRef.current = false;
        pauseStartedAtRef.current = null;
        pausedDurationRef.current = 0;
        setNumbers(shuffle(Array.from({ length: maxNumber }, (_, index) => index + 1)));
        setNextNumber(1);
        setMistakes(0);
        setStartedAt(null);
        setFinishedAt(null);
        setTone('ready');
    };

    const tapNumber = (number) => {
        if (isPaused || done) {
            return;
        }

        if (!startedAt) {
            setStartedAt(Date.now());
        }

        if (number === nextNumber) {
            if (nextNumber === maxNumber) {
                setFinishedAt(Date.now());
                setTone('good');
            } else {
                setTone('good');
            }
            setNextNumber((value) => value + 1);
            return;
        }

        setMistakes((value) => value + 1);
        setTone('miss');
    };

    useEffect(() => {
        if (!startedAt || finishedAt) {
            pauseStartedAtRef.current = null;
            return;
        }

        if (isPaused && pauseStartedAtRef.current === null) {
            pauseStartedAtRef.current = Date.now();
        } else if (!isPaused && pauseStartedAtRef.current !== null) {
            pausedDurationRef.current += Math.max(0, Date.now() - pauseStartedAtRef.current);
            pauseStartedAtRef.current = null;
        }
    }, [finishedAt, isPaused, startedAt]);

    const clockNow = isPaused && pauseStartedAtRef.current !== null
        ? pauseStartedAtRef.current
        : (finishedAt || now);
    const elapsedMs = startedAt
        ? Math.max(0, clockNow - startedAt - pausedDurationRef.current)
        : 0;

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.({ elapsedMs, level, mistakes });
    }, [done, elapsedMs, level, mistakes, onRunComplete]);

    return (
        <GameShell
            {...props}
            copy={copy}
            disabled={isPaused}
            metrics={{ elapsedMs, level, mistakes }}
            tone={done ? 'good' : tone}
        >
            <div className="UFOV-brain-games__game-cue">{copy.schulteCue}</div>
            <div
                className="UFOV-brain-games__schulte-grid"
                style={{ gridTemplateColumns: `repeat(${sideLength}, minmax(0, 1fr))` }}
            >
                {numbers.map((number) => (
                    <button
                        className={number < nextNumber ? 'is-done' : ''}
                        disabled={isPaused || done || number < nextNumber}
                        key={number}
                        type="button"
                        onClick={() => tapNumber(number)}
                    >
                        {number}
                    </button>
                ))}
            </div>
            {done && showResults ? (
                <GameResultOverlay metrics={{ elapsedMs, level, mistakes }} onRestart={reset} />
            ) : null}
        </GameShell>
    );
}

export default SchulteTableGame;
